// Toaster — fixed-position overlay that renders transient notifications.
//
// Mounted once by the app shell; subscribes to `useToast()` so every
// `toast({ ... })` call anywhere in the tree produces a visible card.

import { useToast, type ToastVariant } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { CheckCircle2, Info, TriangleAlert, XCircle, X } from 'lucide-react'

const VARIANT_CLASS: Record<ToastVariant, string> = {
  default: 'border-border bg-background text-foreground',
  success:
    'border-green-500/50 bg-green-50 text-green-900 dark:bg-green-950/40 dark:text-green-100',
  warning:
    'border-yellow-500/50 bg-yellow-50 text-yellow-900 dark:bg-yellow-950/40 dark:text-yellow-100',
  destructive:
    'border-destructive/60 bg-destructive/10 text-destructive-foreground dark:bg-red-950/40 dark:text-red-100',
}

const VARIANT_ICON: Record<ToastVariant, React.ComponentType<{ className?: string }>> = {
  default: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  destructive: XCircle,
}

export function Toaster() {
  const { toasts, dismiss } = useToast()

  if (toasts.length === 0) return null

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((t) => {
        const variant = (t.variant ?? 'default') as ToastVariant
        const Icon = VARIANT_ICON[variant]
        return (
          <div
            key={t.id}
            role="status"
            className={cn(
              'pointer-events-auto flex items-start gap-3 rounded-md border p-3 shadow-lg transition-all',
              VARIANT_CLASS[variant],
            )}
          >
            <Icon className="h-5 w-5 mt-0.5 shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
              {t.title ? (
                <p className="text-sm font-semibold leading-tight">{t.title}</p>
              ) : null}
              {t.description ? (
                <p className="mt-0.5 text-sm opacity-90">{t.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
